import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { changePassword, update } from '../../services/userServices';
import apiClient from '../../utils/api-client';

const ProfileSetting = () => {
  const userId = localStorage.getItem('userID');
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('userData'));

  const [cities, setCities] = useState([]);
  const [isUpdatingInfo, setIsUpdatingInfo] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // Form for updating profile info
  const {
    register: infoRegister,
    handleSubmit: handleInfoSubmit,
    setValue,
  } = useForm();

  // Form for updating password
  const {
    register: passRegister,
    handleSubmit: handlePassSubmit,
    reset: resetPassword,
  } = useForm();

  // Redirect if not logged in
  useEffect(() => {
    if (!userId || !userData) {
      toast.info('Please log in to access your profile settings.');
      navigate('/login');
      return;
    }

    // Fetch user data
    apiClient
      .get(`/user/${userId}`)
      .then(() => {
        setValue('user_id', userData?.user_id);
        setValue('full_name', userData?.full_name);
        setValue('email', userData?.email);
        setValue('phone', userData?.phone);
        setValue('city', userData?.city_id_fk);
        setValue('address', userData?.address);
      })
      .catch(() => {
        toast.error('Failed to load user data.');
      });
  }, [userId, userData, navigate, setValue]);

  // Fetch cities
  useEffect(() => {
    apiClient
      .get('/city')
      .then((res) => setCities(res.data.data))
      .catch(() => toast.error('Failed to load cities.'));
  }, []);

  // Update profile info
  const onSubmitInfo = (formData) => {
    setIsUpdatingInfo(true);
    update(formData)
      .then((response) => {
        if (response?.data?.status) {
          toast.success(response.data.message);
        } else {
          (response.data.data || []).forEach((err) => toast.error(err));
        }
      })
      .catch((err) => toast.error(err?.response?.data?.data?.[0] || 'Error'))
      .finally(() => setIsUpdatingInfo(false));
  };

  // Change password
  const onSubmitPassword = (formData) => {
    setIsUpdatingPassword(true);
    changePassword({ ...formData, user_id: userData?.user_id })
      .then((response) => {
        if (response?.data?.status) {
          toast.success(response.data.message);
          resetPassword();
        } else {
          (response.data.data || []).forEach((err) => toast.error(err));
        }
      })
      .catch((err) => toast.error(err?.response?.data?.data?.[0] || 'Error'))
      .finally(() => setIsUpdatingPassword(false));
  };

  return (
    <section className="pt-4">
      <div className="row">
        {/* Change Information Card */}
        <div className="col-12 col-lg-6">
          <div className="card mb-4 shadow-sm">
            <div className="card-body p-2 p-md-4">
              <h4 className="card-title mb-4">Change Information</h4>
              <form onSubmit={handleInfoSubmit(onSubmitInfo)}>
                <input type="hidden" {...infoRegister('user_id')} />

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter your full name"
                      {...infoRegister('full_name')}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="you@example.com"
                      {...infoRegister('email')}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="+92 300 1234567"
                      {...infoRegister('phone')}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">City</label>
                    <select
                      className="form-select py-2"
                      {...infoRegister('city')}
                    >
                      <option value="">Select your city</option>
                      {cities.map((city) => (
                        <option key={city.city_id} value={city.city_id}>
                          {city.city_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold">Address</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="House #, Street, Area"
                    {...infoRegister('address')}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-sm text-white w-100"
                  style={{ backgroundColor: '#800080' }}
                  disabled={isUpdatingInfo}
                >
                  {isUpdatingInfo
                    ? 'Changing Information...'
                    : 'Change Information'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="col-12 col-lg-6">
          <div className="card mb-4 shadow-sm">
            <div className="card-body p-2 p-md-4">
              <h4 className="card-title mb-4">Change Password</h4>
              <form onSubmit={handlePassSubmit(onSubmitPassword)}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Old Password</label>
                  <input
                    type="password"
                    placeholder="Enter old password"
                    className="form-control"
                    {...passRegister('old_password')}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">New Password</label>
                  <input
                    type="password"
                    placeholder="Enter new password"
                    className="form-control"
                    {...passRegister('password')}
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    placeholder="Enter confirm password"
                    className="form-control"
                    {...passRegister('confirm_password')}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-sm text-white w-100"
                  style={{ backgroundColor: '#800080' }}
                  disabled={isUpdatingPassword}
                >
                  {isUpdatingPassword
                    ? 'Changing password...'
                    : 'Change password'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileSetting;
